// ═══════════════════════════════════════════════════════════════════════════════
// STRATIQ ANALYTICS — Centralized Event Tracking
// Fires events to GA4 Enhanced Ecommerce + Meta Pixel + Custom Analytics
// ═══════════════════════════════════════════════════════════════════════════════

// Check if we're in browser environment
const isBrowser = typeof window !== "undefined";

// GA4 Event Helper
const fireGA4Event = (eventName, params = {}) => {
  if (!isBrowser) return;
  
  try {
    if (window.gtag) {
      window.gtag("event", eventName, {
        ...params,
        event_category: params.category || "engagement",
        event_label: params.label || "",
        value: params.value || 0,
      });
      console.log(`[GA4] Event: ${eventName}`, params);
    }
  } catch (err) {
    console.warn("[GA4] Error firing event:", err);
  }
};

// GA4 Enhanced Ecommerce Event Helper
const fireGA4Ecommerce = (eventName, ecommerceData = {}) => {
  if (!isBrowser) return;
  
  try {
    if (window.gtag) {
      // Clear previous ecommerce data
      window.gtag("event", "clear_ecommerce_data");
      
      // Fire ecommerce event
      window.gtag("event", eventName, {
        ecommerce: ecommerceData,
      });
      console.log(`[GA4 Ecommerce] Event: ${eventName}`, ecommerceData);
    }
  } catch (err) {
    console.warn("[GA4 Ecommerce] Error firing event:", err);
  }
};

// Meta Pixel Event Helper
const fireMetaPixel = (eventName, params = {}) => {
  if (!isBrowser) return;
  
  try {
    if (window.fbq) {
      // Map to standard Meta events where applicable
      const standardEvents = {
        "smartmatch_start": "InitiateCheckout",
        "smartmatch_complete": "Lead",
        "checkout_start": "InitiateCheckout",
        "checkout_complete": "Purchase",
        "lead_captured": "Lead",
        "cta_click": "ViewContent",
        "system_builder_start": "AddToCart",
        "chat_opened": "Contact",
      };
      
      const metaEventName = standardEvents[eventName] || eventName;
      
      // Use trackCustom for non-standard events
      if (standardEvents[eventName]) {
        window.fbq("track", metaEventName, params);
      } else {
        window.fbq("trackCustom", eventName, params);
      }
      console.log(`[Meta Pixel] Event: ${metaEventName}`, params);
    }
  } catch (err) {
    console.warn("[Meta Pixel] Error firing event:", err);
  }
};

// Backend Analytics Event (optional - sends to your own analytics endpoint)
const fireBackendEvent = async (eventName, params = {}) => {
  if (!isBrowser) return;
  
  try {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;
    
    await fetch(`${API}/api/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type: eventName.toUpperCase().replace(/-/g, "_"),
        page_url: window.location.href,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        metadata: params,
      }),
    });
  } catch (err) {
    // Silently fail - don't block user experience for analytics
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// UNIFIED TRACKING FUNCTION
// ═══════════════════════════════════════════════════════════════════════════════

export const trackEvent = (eventName, params = {}) => {
  // Fire to all platforms simultaneously
  fireGA4Event(eventName, params);
  fireMetaPixel(eventName, params);
  fireBackendEvent(eventName, params);
};

// ═══════════════════════════════════════════════════════════════════════════════
// PRE-DEFINED EVENT FUNCTIONS (for type safety and consistency)
// ═══════════════════════════════════════════════════════════════════════════════

// SmartMatch Events
export const trackSmartMatchStart = () => {
  trackEvent("smartmatch_start", {
    category: "smartmatch",
    label: "quiz_started",
  });
};

export const trackSmartMatchStepComplete = (step, stepName) => {
  trackEvent("smartmatch_step_complete", {
    category: "smartmatch",
    label: stepName,
    step_number: step,
  });
};

export const trackSmartMatchComplete = (data = {}) => {
  trackEvent("smartmatch_complete", {
    category: "smartmatch",
    label: "quiz_completed",
    recommended_tier: data.recommendedTier || "",
    business_name: data.businessName || "",
    industry: data.industry || "",
    value: data.tierPrice || 0,
  });
};

// CTA Click Events
export const trackCTAClick = (ctaName, location, destination = "") => {
  trackEvent("cta_click", {
    category: "engagement",
    label: ctaName,
    cta_location: location,
    destination_url: destination,
  });
};

// System Builder Events
export const trackSystemBuilderStart = (recommendedTier = "") => {
  trackEvent("system_builder_start", {
    category: "conversion",
    label: "builder_opened",
    recommended_tier: recommendedTier,
  });
};

export const trackSystemBuilderTierSelected = (tierId, tierName, price) => {
  trackEvent("system_builder_tier_selected", {
    category: "conversion",
    label: tierName,
    tier_id: tierId,
    value: price,
  });
};

export const trackSystemBuilderAddonToggled = (addonId, addonName, isAdded) => {
  trackEvent("system_builder_addon_toggle", {
    category: "conversion",
    label: addonName,
    addon_id: addonId,
    action: isAdded ? "added" : "removed",
  });
};

export const trackSystemBuilderStepComplete = (step) => {
  trackEvent("system_builder_step_complete", {
    category: "conversion",
    label: `step_${step}`,
    step_number: step,
  });
};

// Checkout Events
export const trackCheckoutStart = (items = [], total = 0) => {
  trackEvent("checkout_start", {
    category: "conversion",
    label: "checkout_initiated",
    items_count: items.length,
    value: total,
    currency: "GBP",
  });
};

export const trackCheckoutComplete = (orderId = "", total = 0, items = []) => {
  trackEvent("checkout_complete", {
    category: "conversion",
    label: "purchase_complete",
    transaction_id: orderId,
    value: total,
    currency: "GBP",
    items_count: items.length,
  });
};

// Chat Events
export const trackChatOpened = (location = "") => {
  trackEvent("chat_opened", {
    category: "engagement",
    label: "ai_assistant_opened",
    chat_location: location,
  });
};

export const trackChatMessage = (isUserMessage = true) => {
  trackEvent("chat_message", {
    category: "engagement",
    label: isUserMessage ? "user_message" : "ai_response",
  });
};

// Lead Capture Events
export const trackLeadCaptured = (source = "", data = {}) => {
  trackEvent("lead_captured", {
    category: "conversion",
    label: source,
    lead_source: source,
    has_email: !!data.email,
    has_phone: !!data.phone,
    business_name: data.businessName || "",
  });
};

// Page View Events
export const trackPageView = (pageName, pageUrl = "") => {
  trackEvent("page_view", {
    category: "navigation",
    label: pageName,
    page_url: pageUrl || window.location.href,
  });
};

// Scroll Depth Events
export const trackScrollDepth = (percentage) => {
  trackEvent("scroll_depth", {
    category: "engagement",
    label: `${percentage}%`,
    scroll_percentage: percentage,
  });
};

// Exit Intent Events
export const trackExitIntent = (action = "shown") => {
  trackEvent("exit_intent", {
    category: "engagement",
    label: action, // "shown", "dismissed", "converted"
  });
};

// Sticky Bar Events
export const trackStickyBarInteraction = (action = "shown") => {
  trackEvent("sticky_bar", {
    category: "engagement",
    label: action, // "shown", "clicked", "dismissed"
  });
};

// ═══════════════════════════════════════════════════════════════════════════════
// GA4 ENHANCED ECOMMERCE TRACKING
// Full funnel visibility for conversion analysis
// ═══════════════════════════════════════════════════════════════════════════════

// Format item for GA4 ecommerce
const formatEcommerceItem = (item, index = 0) => ({
  item_id: item.id || item.slug || `item_${index}`,
  item_name: item.name || item.title || "Unknown Item",
  item_brand: "STRATIQ",
  item_category: item.category || (item.type === "addon" ? "Add-On" : "SmartSite"),
  item_category2: item.subcategory || "",
  item_variant: item.variant || "",
  price: parseFloat(item.price) || 0,
  quantity: item.quantity || 1,
  index: index,
});

// View item list (when user sees products)
export const trackViewItemList = (items = [], listName = "SmartSite Packages") => {
  const formattedItems = items.map((item, i) => formatEcommerceItem(item, i));
  
  fireGA4Ecommerce("view_item_list", {
    item_list_id: listName.toLowerCase().replace(/\s+/g, "_"),
    item_list_name: listName,
    items: formattedItems,
  });
  
  // Also fire to Meta Pixel
  fireMetaPixel("ViewContent", {
    content_type: "product_group",
    content_name: listName,
    num_items: items.length,
  });
};

// Select item (when user clicks on a product)
export const trackSelectItem = (item, listName = "SmartSite Packages") => {
  fireGA4Ecommerce("select_item", {
    item_list_id: listName.toLowerCase().replace(/\s+/g, "_"),
    item_list_name: listName,
    items: [formatEcommerceItem(item, 0)],
  });
};

// View item (when user views product details)
export const trackViewItem = (item) => {
  fireGA4Ecommerce("view_item", {
    currency: "GBP",
    value: parseFloat(item.price) || 0,
    items: [formatEcommerceItem(item, 0)],
  });
  
  fireMetaPixel("ViewContent", {
    content_type: "product",
    content_name: item.name || item.title,
    content_ids: [item.id || item.slug],
    value: parseFloat(item.price) || 0,
    currency: "GBP",
  });
};

// Add to cart
export const trackAddToCart = (item, quantity = 1) => {
  const formattedItem = formatEcommerceItem(item, 0);
  formattedItem.quantity = quantity;
  
  fireGA4Ecommerce("add_to_cart", {
    currency: "GBP",
    value: (parseFloat(item.price) || 0) * quantity,
    items: [formattedItem],
  });
  
  fireMetaPixel("AddToCart", {
    content_type: "product",
    content_name: item.name || item.title,
    content_ids: [item.id || item.slug],
    value: (parseFloat(item.price) || 0) * quantity,
    currency: "GBP",
  });
};

// Remove from cart
export const trackRemoveFromCart = (item, quantity = 1) => {
  const formattedItem = formatEcommerceItem(item, 0);
  formattedItem.quantity = quantity;
  
  fireGA4Ecommerce("remove_from_cart", {
    currency: "GBP",
    value: (parseFloat(item.price) || 0) * quantity,
    items: [formattedItem],
  });
};

// View cart
export const trackViewCart = (items = [], cartTotal = 0) => {
  const formattedItems = items.map((item, i) => formatEcommerceItem(item, i));
  
  fireGA4Ecommerce("view_cart", {
    currency: "GBP",
    value: cartTotal,
    items: formattedItems,
  });
};

// Begin checkout (when user starts checkout flow)
export const trackBeginCheckout = (items = [], cartTotal = 0, coupon = "") => {
  const formattedItems = items.map((item, i) => formatEcommerceItem(item, i));
  
  fireGA4Ecommerce("begin_checkout", {
    currency: "GBP",
    value: cartTotal,
    coupon: coupon,
    items: formattedItems,
  });
  
  fireMetaPixel("InitiateCheckout", {
    content_type: "product",
    content_ids: items.map(item => item.id || item.slug),
    value: cartTotal,
    currency: "GBP",
    num_items: items.length,
  });
};

// Add shipping info (checkout step)
export const trackAddShippingInfo = (items = [], cartTotal = 0, shippingTier = "standard") => {
  const formattedItems = items.map((item, i) => formatEcommerceItem(item, i));
  
  fireGA4Ecommerce("add_shipping_info", {
    currency: "GBP",
    value: cartTotal,
    shipping_tier: shippingTier,
    items: formattedItems,
  });
};

// Add payment info (checkout step)
export const trackAddPaymentInfo = (items = [], cartTotal = 0, paymentType = "card") => {
  const formattedItems = items.map((item, i) => formatEcommerceItem(item, i));
  
  fireGA4Ecommerce("add_payment_info", {
    currency: "GBP",
    value: cartTotal,
    payment_type: paymentType,
    items: formattedItems,
  });
  
  fireMetaPixel("AddPaymentInfo", {
    content_type: "product",
    value: cartTotal,
    currency: "GBP",
  });
};

// Purchase complete (conversion!)
export const trackPurchase = (transactionId, items = [], total = 0, tax = 0, shipping = 0, coupon = "") => {
  const formattedItems = items.map((item, i) => formatEcommerceItem(item, i));
  
  fireGA4Ecommerce("purchase", {
    transaction_id: transactionId,
    affiliation: "STRATIQ Solutions",
    value: total,
    tax: tax,
    shipping: shipping,
    currency: "GBP",
    coupon: coupon,
    items: formattedItems,
  });
  
  fireMetaPixel("Purchase", {
    content_type: "product",
    content_ids: items.map(item => item.id || item.slug),
    value: total,
    currency: "GBP",
    num_items: items.length,
  });
  
  // Also fire standard event for backwards compatibility
  trackEvent("checkout_complete", {
    category: "conversion",
    label: "purchase_complete",
    transaction_id: transactionId,
    value: total,
    currency: "GBP",
    items_count: items.length,
  });
};

// Funnel step tracking (for custom funnel analysis)
export const trackFunnelStep = (stepNumber, stepName, additionalData = {}) => {
  trackEvent("funnel_step", {
    category: "funnel",
    label: stepName,
    step_number: stepNumber,
    funnel_name: additionalData.funnelName || "main_funnel",
    ...additionalData,
  });
  
  // Fire as conversion event for high-value steps
  if (stepNumber >= 3) {
    fireGA4Event("conversion", {
      send_to: "conversion",
      event_callback: () => {},
      step: stepNumber,
      step_name: stepName,
    });
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// DEBUG MODE
// ═══════════════════════════════════════════════════════════════════════════════

export const enableAnalyticsDebug = () => {
  if (isBrowser) {
    window.__STRATIQ_ANALYTICS_DEBUG__ = true;
    console.log("[Analytics] Debug mode enabled - all events will be logged to console");
  }
};

export const disableAnalyticsDebug = () => {
  if (isBrowser) {
    window.__STRATIQ_ANALYTICS_DEBUG__ = false;
  }
};

// Default export
export default {
  trackEvent,
  trackSmartMatchStart,
  trackSmartMatchStepComplete,
  trackSmartMatchComplete,
  trackCTAClick,
  trackSystemBuilderStart,
  trackSystemBuilderTierSelected,
  trackSystemBuilderAddonToggled,
  trackSystemBuilderStepComplete,
  trackCheckoutStart,
  trackCheckoutComplete,
  trackChatOpened,
  trackChatMessage,
  trackLeadCaptured,
  trackPageView,
  trackScrollDepth,
  trackExitIntent,
  trackStickyBarInteraction,
  // GA4 Enhanced Ecommerce
  trackViewItemList,
  trackSelectItem,
  trackViewItem,
  trackAddToCart,
  trackRemoveFromCart,
  trackViewCart,
  trackBeginCheckout,
  trackAddShippingInfo,
  trackAddPaymentInfo,
  trackPurchase,
  trackFunnelStep,
  enableAnalyticsDebug,
  disableAnalyticsDebug,
};
